// Globals
import { useReducer, useRef, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import memoizeOne from 'memoize-one';
import invariant from 'tiny-invariant';
import { triggerPostMoveFlash } from '@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash';
import { type Instruction, type ItemMode } from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';
import * as liveRegion from '@atlaskit/pragmatic-drag-and-drop-live-region';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

// Store
import { useAppState } from '@/store';
import { categoriesSelector } from '../../feature/selectors';

// Components
import { StyledSection, StyledSectionWrapper } from '../../components/Styled';
import TreeItem from './TreeItem';

// Helpers
import { DependencyContext, TreeContextValue, TreeContext } from './treeContext';
import { treeStateReducer, tree, TreeItem as TreeItemType, getInitialTreeState } from './data';
import { CategoriesAPI } from '@/api';

type CleanupFn = () => void;

const createTreeItemRegistry = () => {
  const registry = new Map<
    string,
    {
      element: HTMLElement;
    }
  >();

  const registerTreeItem = ({ itemId, element }: { itemId: string; element: HTMLElement }): CleanupFn => {
    registry.set(itemId, {
      element,
    });
    return () => {
      registry.delete(itemId);
    };
  };

  return { registry, registerTreeItem };
};

const calculateNewPosition = (
  data: TreeItemType[],
  itemId: string,
  targetId: string,
  instruction: Instruction,
): number => {
  const targetItem = tree.find(data, targetId);
  invariant(targetItem);

  const parentId = tree.getPathToItem({ current: data, targetId: targetId })?.at(-1) || '';
  const parentItem = parentId ? tree.find(data, parentId) : null;
  const siblings = parentItem ? parentItem.children : data;

  // Find the original position of the item being moved
  const originalPosition = siblings.findIndex((item) => item.id === itemId);

  let newPosition = siblings.findIndex((item) => item.id === targetId);

  if (instruction.type === 'reorder-above') {
    // Insert above the target
    newPosition = newPosition;
  } else if (instruction.type === 'reorder-below') {
    // Insert below the target
    newPosition = newPosition + 1;
  }

  // If item is moving below, adjust the position to avoid incorrect data
  if (
    newPosition > originalPosition &&
    (instruction.type === 'reorder-above' || instruction.type === 'reorder-below')
  ) {
    newPosition = newPosition - 1;
  }

  return newPosition;
};

// TODO: needs overhaul and simplification
const EditCategoriesPositionsContainer = () => {
  const { categories } = useAppState(categoriesSelector);
  const categoriesState = useMemo(() => getInitialTreeState(categories!), [categories]);
  const [state, updateState] = useReducer(treeStateReducer, categoriesState);

  const ref = useRef<HTMLDivElement>(null);
  const { extractInstruction } = useContext(DependencyContext);

  const [{ registry, registerTreeItem }] = useState(createTreeItemRegistry);

  const { data: treeData, lastAction } = state;

  const lastStateRef = useRef<TreeItemType[]>(treeData);
  useEffect(() => {
    lastStateRef.current = treeData;
  }, [treeData]);

  useEffect(() => {
    if (lastAction === null) {
      return;
    }

    if (lastAction?.type === 'instruction') {
      const { element } = registry.get(lastAction.itemId) ?? {};
      if (element) {
        triggerPostMoveFlash(element);
      }

      return;
    }
  }, [lastAction, registry]);

  useEffect(() => {
    return () => {
      liveRegion.cleanup();
    };
  }, []);

  /**
   * Returns the items that the item with `itemId` can be moved to.
   *
   * Uses a depth-first search (DFS) to compile a list of possible targets.
   */
  const getMoveTargets = useCallback(({ itemId }: { itemId: string }) => {
    const dataInner = lastStateRef.current;

    const targets = [];

    const searchStack = Array.from(dataInner);
    while (searchStack.length > 0) {
      const node = searchStack.pop();

      if (!node) {
        continue;
      }

      /**
       * If the current node is the item we want to move, then it is not a valid
       * move target and neither are its children.
       */
      if (node.id === itemId) {
        continue;
      }

      targets.push(node);

      node.children.forEach((childNode) => searchStack.push(childNode));
    }

    return targets;
  }, []);

  const getChildrenOfItem = useCallback((itemId: string) => {
    const dataInner = lastStateRef.current;

    /**
     * An empty string is representing the root
     */
    if (itemId === '') {
      return dataInner;
    }

    const item = tree.find(dataInner, itemId);
    invariant(item);
    return item.children;
  }, []);

  const context = useMemo<TreeContextValue>(
    () => ({
      dispatch: updateState,
      uniqueContextId: Symbol('unique-id'),
      // memoizing this function as it is called by all tree items repeatedly
      // An ideal refactor would be to update our data shape
      // to allow quick lookups of parents
      getPathToItem: memoizeOne(
        (targetId: string) => tree.getPathToItem({ current: lastStateRef.current, targetId }) ?? [],
      ),
      getMoveTargets,
      getChildrenOfItem,
      registerTreeItem,
    }),
    [getChildrenOfItem, getMoveTargets, registerTreeItem],
  );

  useEffect(() => {
    invariant(ref.current);
    return combine(
      monitorForElements({
        canMonitor: ({ source }) => source.data.uniqueContextId === context.uniqueContextId,
        onDrop(args) {
          const { location, source } = args;
          // didn't drop on anything
          if (!location.current.dropTargets.length) {
            return;
          }

          if (source.data.type === 'tree-item') {
            const itemId = source.data.id as string;

            const target = location.current.dropTargets[0];
            const targetId = target.data.id as string;

            const instruction: Instruction | null = extractInstruction(target.data);

            if (instruction !== null) {
              updateState({
                type: 'instruction',
                instruction,
                itemId,
                targetId,
              });

              const newPosition = calculateNewPosition(treeData, itemId, targetId, instruction);

              CategoriesAPI.changeCategoryPosition(itemId, newPosition);
            }
          }
        },
      }),
    );
  }, [context, extractInstruction, treeData]);

  return (
    <TreeContext.Provider value={context}>
      <StyledSectionWrapper id="tree" ref={ref}>
        {treeData.map((item, index, array) => {
          const type: ItemMode = (() => {
            if (item.children.length && item.isOpen) {
              return 'expanded';
            }

            if (index === array.length - 1) {
              return 'last-in-group';
            }

            return 'standard';
          })();

          return (
            <StyledSection key={item.id}>
              <TreeItem item={item} level={0} mode={type} index={index} />
            </StyledSection>
          );
        })}
      </StyledSectionWrapper>
    </TreeContext.Provider>
  );
};

export default EditCategoriesPositionsContainer;
